// useStaffRatingApi.js (snippets)
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* -------- helpers -------- */
async function safeJson(res) {
  const ct = res.headers.get("content-type") || "";
  const txt = await res.text();
  if (!ct.includes("application/json")) return null; // <- don't try to JSON.parse HTML
  try {
    return JSON.parse(txt);
  } catch {
    return null;
  }
}

async function tryFetch(urls, opts) {
  const list = Array.isArray(urls) ? urls : [urls];
  let lastErr;
  for (const u of list) {
    try {
      const res = await fetch(u, { mode: "cors", ...opts });
      if (res.ok) return res;
      lastErr = new Error(`HTTP ${res.status} for ${u}`);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("All fetch attempts failed");
}

export function useStaffRatingApi({
  apiBase,
  selectedUser,
  selectedProject,
  period,
  include,
  from,
  to,
}) {
  const [base, setBase] = useState(apiBase || "https://jkumarapi.axiomos.in");
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const endpoints = useRef({
    users: ["/user"],
    projects: ["/projects"], // <- try plural first
    ratingList: ["/rating/live"],
  });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set("role", "all");
    if (["month", "quarter", "year"].includes(period))
      params.set("period", period);
    else if (period === "ytd") {
      params.set(
        "from",
        new Date(new Date().getFullYear(), 0, 1).toISOString()
      );
      params.set("to", new Date().toISOString());
    } else if (period === "lifelong") {
      params.set("from", "1970-01-01T00:00:00.000Z");
      params.set("to", new Date().toISOString());
    } else if (period === "custom") {
      if (from) params.set("from", new Date(from).toISOString());
      if (to) params.set("to", new Date(`${to}T23:59:59`).toISOString());
    } else params.set("period", "month");
    if (include && include !== "none") params.set("include", include);
    if (selectedProject) params.set("project", selectedProject);
    return params.toString();
  }, [period, include, selectedProject, from, to]);

  const loadMeta = useCallback(async () => {
    try {
      // users
      const uRes = await tryFetch(
        endpoints.current.users.map((p) => `${base}${p}?isActive=true`)
      );
      const uJson = uRes.ok ? await safeJson(uRes) : null;
      setUsers(
        Array.isArray(uJson) ? uJson : uJson?.data || uJson?.results || []
      );

      // projects (try plural → singular)
      const pRes = await tryFetch(
        endpoints.current.projects.map((p) => `${base}${p}`)
      );
      const pJson = pRes.ok ? await safeJson(pRes) : null;
      setProjects(
        Array.isArray(pJson) ? pJson : pJson?.data || pJson?.results || []
      );
    } catch (e) {
      console.warn("Filter meta load failed:", e);
    }
  }, [base]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = buildQuery();
      const candidates = endpoints.current.ratingList.map((p) =>
        selectedUser && selectedUser !== "all"
          ? `${base}${p}/${selectedUser}?${qs}`
          : `${base}${p}?${qs}`
      );
      const res = await tryFetch(candidates);
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setData(null);
        return;
      }
      const json = await safeJson(res);
      if (!json) {
        setError("Invalid JSON received");
        setData(null);
        return;
      }
      setData(json);
    } catch (e) {
      setError(e?.message || "Network error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [base, buildQuery, selectedUser]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const isSingle = useMemo(
    () => selectedUser && selectedUser !== "all",
    [selectedUser]
  );
  const listResults = useMemo(() => {
    if (!data || isSingle) return [];
    return Array.isArray(data)
      ? data
      : Array.isArray(data?.results)
      ? data.results
      : [];
  }, [data, isSingle]);
  const single = useMemo(
    () => (!data || !isSingle ? null : data),
    [data, isSingle]
  );

  return {
    users,
    projects,
    data,
    loading,
    error,
    listResults,
    single,
    refetch: fetchData,
    setApiBase: setBase,
  };
}
